import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { GateForm } from './gate-form';
import {
	buildCreateGateInput,
	generateIdempotentGateId,
	type GateFormSubmitData
} from './gate-input';
import {
	GATE_CREATE_MUTATION,
	TERMINAL_TYPES_QUERY
} from './gates.gql.tsx';

interface CloneData {
	name?: string | null;
	ipAddress?: string | null;
	gateKey?: string | null;
	terminalTypeId?: number | null;
	isActive?: boolean;
}

interface LocationState {
	cloneData?: CloneData;
}

type TerminalTypeOption = {
	terminalTypeId: number;
	name?: string | null;
};

export const GatesCreate = () => {
	const { t } = useTranslation();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const idempotentTerminalId = useMemo(
		() => generateIdempotentGateId(),
		[]
	);

	const {
		data: terminalTypesData,
		loading: isTerminalTypeLoading,
		error: terminalTypesError
	} = useQuery<{ terminalTypes: TerminalTypeOption[] }>(TERMINAL_TYPES_QUERY);

	const terminalTypeOptions = useMemo(
		() =>
			(terminalTypesData?.terminalTypes ?? [])
				.filter(type => type?.terminalTypeId != null)
				.map(type => ({
					value: String(type.terminalTypeId),
					label: type.name ?? `Type ${type.terminalTypeId}`
				})),
		[terminalTypesData]
	);

	const [createGate, { loading: isSubmitting }] = useMutation(
		GATE_CREATE_MUTATION
	);

	useEffect(() => {
		setPageHeader({
			title: t('gates:create.title'),
			description: t('gates:create.description'),
			showBackButton: true,
			onBackClick: () => go({ to: '/gates' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, t]);

	const handleSubmit = async (formData: GateFormSubmitData) => {
		const payload = buildCreateGateInput(formData, idempotentTerminalId);

		try {
			const { data } = await createGate(
				getMutationOptions('gates', { input: payload })
			);

			const message = extractCheckResultError(data?.createTerminal);

			if (message) {
				showMutationErrorToast('gates', 'create', { message });
				return;
			}

			showMutationSuccessToast('gates', 'create');
			go({
				to: '/gates',
				state: { selectGateId: idempotentTerminalId }
			});
		} catch (error) {
			showMutationErrorToast('gates', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			{terminalTypesError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to load gate types</AlertTitle>
					<AlertDescription>
						{terminalTypesError.message}
					</AlertDescription>
				</Alert>
			)}

			<GateForm
				mode={cloneData ? 'clone' : 'create'}
				initialData={cloneData ? {
					name: cloneData.name ?? '',
					ipAddress: cloneData.ipAddress ?? '',
					gateKey: cloneData.gateKey ?? '',
					terminalTypeId: cloneData.terminalTypeId?.toString() ?? '',
					isActive: cloneData.isActive ?? true
				} : undefined}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				terminalTypeOptions={terminalTypeOptions}
				isTerminalTypeLoading={isTerminalTypeLoading}
				headerTitle={t('gates:create.title')}
			/>
		</FormPageLayout>
	);
};
